import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as Tabs from '@radix-ui/react-tabs';
import { FileDown, FileSpreadsheet, MoreHorizontal, Play, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { api, download, getToken } from '@/lib/api';
import { dateShortcut, formatDate, parseDateInput } from '@/lib/format';
import type { ListResult } from '@/lib/types';
import { Spinner, ErrorBox } from '@/ui/Spinner';
import { DataGrid } from '@/grid/DataGrid';
import { LookupDialog } from '@/grid/LookupDialog';
import { confirmDialog } from '@/ui/confirm';
import { sortRowsLocal, filterRowsLocal } from './local';
import { DateInput } from '@/ui/DateInput';

interface Param { id: number; name: string; type: string; default: string | null; source: string | null; sourceKind: string; field: string | null; filter: string | null; hidden: boolean; active: boolean }
interface Def {
  id: number; name: string; code: string | null; group: string | null; description: string | null; excel: boolean; print: boolean; message: boolean; ftp: boolean;
  askParams: boolean; hasLayout: boolean; datasets: number[]; params: Param[]; canExport: boolean;
}
interface RunResult { name: string; datasets: { index: number; name: string; columns: any[]; rows: any[]; truncated?: boolean }[]; message: string | null }

const isDate = (p: Param) => p.type.toLocaleLowerCase('tr-TR') === 'tarih';

/** "dd.mm.yyyy" veya ISO → input[type=date] değeri */
function toDateInput(v: string | null | undefined) {
  if (!v) return '';
  const iso = parseDateInput(v);
  return iso ? iso.slice(0, 10) : '';
}

/**
 * Rapor çalıştırıcı (Delphi Form901): parametreler BilsanRapor_Params'tan; tarih kısayolları (Bugün, Aybaşı …) açılışta uygulanır,
 * lookup'lar görünüm/hazır SQL'den; sonuç şablon varsa sayfalı önizleme (yazdır/PDF), yoksa veri tablosu (Excel/CSV).
 */
export function ReportRunner({ reportId, initial }: { reportId: number; initial?: Record<string, unknown> }) {
  const def = useQuery({ queryKey: ['report-def', reportId], queryFn: () => api<Def>(`/reports/${reportId}`) });
  const [values, setValues] = useState<Record<string, string>>({});
  const [applied, setApplied] = useState<Record<string, string> | null>(null);
  const [result, setResult] = useState<RunResult | null>(null);
  const [html, setHtml] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState<'preview' | 'data'>('preview');
  const [lookup, setLookup] = useState<Param | null>(null);
  const frame = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (!def.data) return;
    const v: Record<string, string> = {};
    for (const p of def.data.params) {
      let d = p.default ?? '';
      // Delphi MSQuery2AfterOpen: tarih kısayolu parametrelerine açılışta bugünün değeri yazılır
      if (p.sourceKind === 'dateShortcut') d = formatDate(dateShortcut(p.source) ?? '', false);
      v[p.name] = d;
    }
    for (const [k, val] of Object.entries(initial ?? {})) {
      const p = def.data.params.find((x) => x.name.toLocaleUpperCase('tr-TR') === k.toLocaleUpperCase('tr-TR'));
      if (p && val != null) v[p.name] = isDate(p) ? formatDate(val, false) : String(val);
    }
    setValues(v);
    setView(def.data.hasLayout ? 'preview' : 'data');
    // Delphi: parametre yoksa veya PARAMETRE işaretliyse doğrudan çalıştır
    if (!def.data.params.length || def.data.askParams) run(v);
  }, [def.data]);

  const visibleParams = useMemo(() => (def.data?.params ?? []).filter((p) => p.active !== false), [def.data]);

  const run = async (v = values) => {
    if (!def.data) return;
    if (def.data.params.some((p) => p.sourceKind === 'confirm') && !(await confirmDialog('Dikkat..! Bu İşlem Yeterli Bilgi Gerektirir Devam etmek İstiyormusunuz ?'))) return;
    setBusy(true);
    setHtml(null);
    try {
      const body = { params: v };
      const r = await api<RunResult>(`/reports/${reportId}/run`, { body });
      setResult(r);
      setApplied(v);
      if (r.message) await confirmDialog(r.message, { title: def.data.name, cancel: 'Kapat', ok: 'Tamam' });
      if (def.data.hasLayout) {
        const res = await fetch(`/api/reports/${reportId}/render`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` }, body: JSON.stringify(body) });
        const text = await res.text();
        if (!res.ok) throw new Error(safeMessage(text));
        setHtml(text);
      }
    } catch (e: any) {
      toast.error(e.message ?? 'Rapor çalıştırılamadı');
    } finally {
      setBusy(false);
    }
  };

  if (def.isLoading) return <Spinner />;
  if (def.error) return <ErrorBox error={def.error} />;
  const d = def.data!;

  return (
    <div className="flex min-h-0 flex-1 gap-2 p-2">
      <div className="panel flex w-72 shrink-0 flex-col rounded">
        <div className="border-b b-border px-3 py-2">
          <div className="font-semibold">{d.name}</div>
          <div className="text-xs muted">{[d.code, d.group].filter(Boolean).join(' · ')}</div>
        </div>
        <form className="flex min-h-0 flex-1 flex-col gap-2.5 overflow-auto p-3 scroll-thin" onSubmit={(e) => { e.preventDefault(); run(); }}>
          {visibleParams.length === 0 && <div className="text-xs muted">Bu raporun parametresi yok.</div>}
          {visibleParams.map((p) => (
            <label key={p.id} className="flex flex-col gap-1">
              <span className="text-xs font-medium muted">{p.name}</span>
              <div className="flex gap-1">
                {isDate(p) ? (
                  <DateInput className="flex-1" value={toDateInput(values[p.name]) ? `${toDateInput(values[p.name])}T00:00:00` : null}
                    onChange={(v) => setValues((x) => ({ ...x, [p.name]: v ? formatDate(v, false) : '' }))} />
                ) : (
                  <input className="inp" type={p.hidden ? 'password' : 'text'} value={values[p.name] ?? ''} onChange={(e) => setValues((x) => ({ ...x, [p.name]: e.target.value }))} />
                )}
                {(p.sourceKind === 'view' || p.sourceKind === 'sql') && (
                  <button type="button" className="btn !px-2" title="Listeden seç" onClick={() => setLookup(p)}><MoreHorizontal size={14} /></button>
                )}
              </div>
            </label>
          ))}
          <button className="btn btn-primary mt-1 justify-center" disabled={busy}><Play size={14} /> {busy ? 'Çalışıyor…' : 'Raporu Çalıştır (F2)'}</button>
          {applied && result && <div className="text-xs muted">{result.datasets.map((x) => `${x.name}: ${x.rows.length} kayıt${x.truncated ? ' (kısaltıldı)' : ''}`).join(' · ')}</div>}
        </form>
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="panel flex flex-wrap items-center gap-1.5 rounded px-2 py-1.5">
          {d.hasLayout && (
            <Tabs.Root value={view} onValueChange={(v) => setView(v as any)}>
              <Tabs.List className="flex gap-1">
                <Tabs.Trigger value="preview" className="btn data-[state=active]:bg-[var(--grid-header)]">Önizleme</Tabs.Trigger>
                <Tabs.Trigger value="data" className="btn data-[state=active]:bg-[var(--grid-header)]">Veri</Tabs.Trigger>
              </Tabs.List>
            </Tabs.Root>
          )}
          <div className="ml-auto flex gap-1.5">
            {d.hasLayout && <button className="btn" disabled={!html} onClick={() => frame.current?.contentWindow?.print()}><Printer size={14} /> Yazdır</button>}
            {d.hasLayout && d.canExport && <button className="btn" disabled={!applied} onClick={() => download(`/reports/${reportId}/pdf`, { params: applied }, `${d.name}.pdf`).catch((e) => toast.error(e.message))}><FileDown size={14} /> PDF</button>}
            {d.canExport && <button className="btn" disabled={!applied} onClick={() => download(`/reports/${reportId}/export`, { params: applied, format: 'xlsx' }, `${d.name}.xlsx`).catch((e) => toast.error(e.message))}><FileSpreadsheet size={14} /> Excel</button>}
            {d.canExport && <button className="btn" disabled={!applied} onClick={() => download(`/reports/${reportId}/export`, { params: applied, format: 'csv' }, `${d.name}.csv`).catch((e) => toast.error(e.message))}>CSV</button>}
          </div>
        </div>
        <div className="panel relative min-h-0 flex-1 overflow-hidden rounded">
          {busy && <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50 dark:bg-black/30"><Spinner label="Rapor hazırlanıyor…" /></div>}
          {!result && !busy && <div className="flex h-full items-center justify-center muted">Parametreleri girip raporu çalıştırın.</div>}
          {result && view === 'preview' && d.hasLayout && html && (
            <iframe ref={frame} title={d.name} className="h-full w-full border-0 bg-[#e5e7eb]" srcDoc={html} sandbox="allow-same-origin allow-modals" />
          )}
          {result && (view === 'data' || !d.hasLayout) && <DatasetTabs result={result} />}
        </div>
      </div>
      {lookup && (
        <LookupDialog open row={values} onClose={() => setLookup(null)}
          spec={{
            title: lookup.name,
            fetch: (q) => api<ListResult>(`/reports/${reportId}/params/${lookup.id}/lookup`, { body: { query: q, values } }),
            apply: (picked) => {
              const f = lookup.field ?? Object.keys(picked)[0];
              const key = Object.keys(picked).find((k) => k.toLocaleUpperCase('tr-TR') === f.toLocaleUpperCase('tr-TR')) ?? f;
              return { [lookup.name]: picked[key] == null ? '' : isDate(lookup) ? formatDate(picked[key], false) : String(picked[key]) };
            },
          }}
          onPick={(v) => setValues((x) => ({ ...x, ...v }))} />
      )}
    </div>
  );
}

function DatasetTabs({ result }: { result: RunResult }) {
  const [active, setActive] = useState(String(result.datasets[0]?.index ?? ''));
  if (!result.datasets.length) return <div className="p-4 muted">Rapor veri kümesi döndürmedi.</div>;
  const grid = (ds: RunResult['datasets'][number]) => (
    <DataGrid key={ds.index} queryKey={['report-data', result.name, ds.index, ds.rows.length]}
      fetch={async (q) => {
        let rows = filterRowsLocal(ds.rows, ds.columns, q);
        if (q.sort?.length) rows = sortRowsLocal(rows, q.sort, ds.columns);
        const off = q.offset ?? 0, lim = q.all ? rows.length : q.limit ?? 200;
        const totals: Record<string, number | null> = {};
        for (const t of q.totals ?? []) totals[`${t.kind}:${t.field}`] = rows.reduce((a, r) => a + (Number(r[t.field]) || 0), 0);
        return { columns: ds.columns, rows: rows.slice(off, off + lim), total: rows.length, totals, mode: 'memory', missingParams: [] };
      }}
      filterRow bare={false} />
  );
  if (result.datasets.length === 1) return <div className="flex h-full flex-col">{grid(result.datasets[0])}</div>;
  return (
    <Tabs.Root value={active} onValueChange={setActive} className="flex h-full flex-col">
      <Tabs.List className="flex gap-1 border-b b-border px-2 pt-1.5">
        {result.datasets.map((ds) => <Tabs.Trigger key={ds.index} value={String(ds.index)} className="rounded-t px-3 py-1 text-[12.5px] muted data-[state=active]:bg-[var(--grid-header)] data-[state=active]:text-[var(--text)]">{ds.name} ({ds.rows.length})</Tabs.Trigger>)}
      </Tabs.List>
      {result.datasets.map((ds) => <Tabs.Content key={ds.index} value={String(ds.index)} className="min-h-0 flex-1 data-[state=inactive]:hidden">{grid(ds)}</Tabs.Content>)}
    </Tabs.Root>
  );
}

function safeMessage(t: string) {
  try { return JSON.parse(t).message; } catch { return t.slice(0, 200); }
}
